import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LogOut, Plus, Trash2, Edit3, Save, X, Loader2, RefreshCw, Globe, Users, MousePointerClick, Calendar, Search, Copy, CheckCircle2, Link2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useAuth() {
  const nav = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('mbe_admin_token') : null;
  useEffect(() => {
    if (!token) nav('/admin');
  }, [token, nav]);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const logout = () => {
    localStorage.removeItem('mbe_admin_token');
    localStorage.removeItem('mbe_admin_user');
    nav('/admin');
  };
  return { token, headers, logout, username: localStorage.getItem('mbe_admin_user') };
}

function Section({ title, children, actions }) {
  return (
    <div className="card-dark p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-white text-lg tracking-tight">{title}</div>
        <div>{actions}</div>
      </div>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-dark p-5">
      <div className="flex items-center gap-3">
        <div className="icon-badge"><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">{label}</div>
          <div className="font-display text-white text-2xl mt-1">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { headers, logout, username } = useAuth();
  const [tab, setTab] = useState('analytics');

  const [analytics, setAnalytics] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    country_code: '',
    cta_source: '',
    utm_source: '',
    utm_campaign: '',
    min_clicks: '',
  });

  // Country editor state
  const [editRow, setEditRow] = useState(null); // { code, name, url, is_active }
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    if (!headers.Authorization) return;
    setLoading(true);
    try {
      const params = { days };
      Object.keys(filters).forEach((k) => { if (filters[k] !== '' && filters[k] != null) params[k] = filters[k]; });
      const [a, c] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers, params }),
        axios.get(`${API}/admin/countries`, { headers }),
      ]);
      setAnalytics(a.data);
      setCountries(c.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [days, filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function saveCountry(row) {
    try {
      await axios.post(`${API}/admin/countries`, {
        code: row.code.toUpperCase(),
        name: row.name,
        url: row.url,
        is_active: row.is_active !== false,
      }, { headers });
      setEditRow(null);
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    }
  }

  async function deleteCountry(code) {
    if (!window.confirm(`Delete mapping for ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/countries/${code}`, { headers });
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  }

  const filteredCountries = countries.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.code || '').toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="font-display text-white text-2xl tracking-tight">MBE Admin Dashboard</div>
            <div className="text-[#f2f2f2] text-[13px]">Signed in as <span className="text-white">{username || 'admin'}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} disabled={loading} className="btn-outline-dark inline-flex items-center gap-2 px-3 py-2 text-[12px]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button onClick={logout} className="btn-outline-dark inline-flex items-center gap-2 px-3 py-2 text-[12px]">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 liquid-glass rounded-full p-1 w-fit">
          {[
            { id: 'analytics', label: 'Analytics' },
            { id: 'ads', label: 'Ads AI Analyst' },
            { id: 'countries', label: 'Country Links' },
            { id: 'builder', label: 'Ad Link Builder' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-1.5 text-[12px] rounded-full uppercase tracking-wider relative z-[1] ${
                tab === t.id ? 'bg-white text-black font-semibold' : 'text-[#f2f2f2] hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'analytics' && analytics && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Page views (period)" value={(analytics.period_pageviews || 0).toLocaleString()} />
              <StatCard icon={MousePointerClick} label="Button clicks (period)" value={analytics.period_clicks.toLocaleString()} />
              <StatCard icon={Calendar} label="Click-through rate" value={`${(analytics.ctr || 0).toFixed(2)}%`} />
              <StatCard icon={Globe} label="Countries reached" value={analytics.by_country.length} />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[#f2f2f2] text-[12px]">Range:</span>
              {[7, 14, 30, 90, 180].map((d) => (
                <button key={d} onClick={() => { setDays(d); setFilters({ ...filters, start_date: '', end_date: '' }); }} className={`px-3 py-1.5 rounded-full text-[12px] ${days === d && !filters.start_date ? 'bg-white text-black font-semibold' : 'liquid-glass text-[#f2f2f2]'}`}>
                  <span className="relative z-[1]">{d}d</span>
                </button>
              ))}
            </div>

            <Section title="Filters" actions={
              <button
                onClick={() => setFilters({ start_date: '', end_date: '', country_code: '', cta_source: '', utm_source: '', utm_campaign: '', min_clicks: '' })}
                className="btn-outline-dark inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px]"
              >
                <X className="w-3.5 h-3.5" /> Reset
              </button>
            }>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Start date</label>
                  <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">End date</label>
                  <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Country</label>
                  <select value={filters.country_code} onChange={(e) => setFilters({ ...filters, country_code: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
                    <option value="" className="bg-[#0b1a35]">All countries</option>
                    {(analytics?.filters_meta?.countries || []).map((c) => (
                      <option key={c.code} value={c.code} className="bg-[#0b1a35]">{c.code} — {c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">CTA source</label>
                  <select value={filters.cta_source} onChange={(e) => setFilters({ ...filters, cta_source: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
                    <option value="" className="bg-[#0b1a35]">All CTA sources</option>
                    {(analytics?.filters_meta?.cta_sources || []).map((s) => (
                      <option key={s} value={s} className="bg-[#0b1a35]">{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">UTM source</label>
                  <select value={filters.utm_source} onChange={(e) => setFilters({ ...filters, utm_source: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
                    <option value="" className="bg-[#0b1a35]">All UTM sources</option>
                    {(analytics?.filters_meta?.utm_sources || []).map((s) => (
                      <option key={s} value={s} className="bg-[#0b1a35]">{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">UTM campaign</label>
                  <select value={filters.utm_campaign} onChange={(e) => setFilters({ ...filters, utm_campaign: e.target.value })} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
                    <option value="" className="bg-[#0b1a35]">All UTM campaigns</option>
                    {(analytics?.filters_meta?.utm_campaigns || []).map((s) => (
                      <option key={s} value={s} className="bg-[#0b1a35]">{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Min clicks (per country)</label>
                  <input type="number" min="0" value={filters.min_clicks} onChange={(e) => setFilters({ ...filters, min_clicks: e.target.value })} placeholder="0" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                </div>
                <div className="md:col-span-3 flex items-end">
                  <div className="text-[#f2f2f2] text-[11px]">Showing <span className="text-white font-semibold">{analytics?.period_clicks?.toLocaleString() || 0}</span> filtered clicks over {analytics?.period_days} days.</div>
                </div>
              </div>
            </Section>

            <Section title="Clicks by country">
              {analytics.by_country.length === 0 ? (
                <div className="text-[#f2f2f2] text-sm">No clicks yet in this period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                        <th className="py-2 pr-4">Country</th>
                        <th className="py-2 pr-4">Code</th>
                        <th className="py-2 pr-4">Clicks</th>
                        <th className="py-2 pr-4">Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.by_country.map((r) => (
                        <tr key={r.code + r.name} className="border-b border-white/5">
                          <td className="py-2 pr-4 text-white">{r.name || '—'}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2]">{r.code}</td>
                          <td className="py-2 pr-4 text-white font-semibold">{r.clicks}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2]">{r.last_seen ? new Date(r.last_seen).toLocaleString() : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* UTM breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <UtmBreakdownTable title="Clicks by UTM Campaign" rows={analytics.utm_campaign || []} />
              <UtmBreakdownTable title="Clicks by UTM Source" rows={analytics.utm_source || []} />
              <UtmBreakdownTable title="Clicks by UTM Content" rows={analytics.utm_content || []} />
              <UtmBreakdownTable title="Clicks by CTA Source (button on page)" rows={analytics.cta_source || []} />
            </div>

            <Section title="Recent clicks (last 25)">
              {analytics.recent.length === 0 ? (
                <div className="text-[#f2f2f2] text-sm">No clicks yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                        <th className="py-2 pr-4">When</th>
                        <th className="py-2 pr-4">Country</th>
                        <th className="py-2 pr-4">City</th>
                        <th className="py-2 pr-4">Source</th>
                        <th className="py-2 pr-4">UTM Campaign</th>
                        <th className="py-2 pr-4">UTM Source</th>
                        <th className="py-2 pr-4">Telegram URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent.map((r, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-2 pr-4 text-[#f2f2f2] whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}</td>
                          <td className="py-2 pr-4 text-white">{r.country_name || r.country_code || '—'}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2]">{r.city || '—'}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2]">{r.cta_source || '—'}</td>
                          <td className="py-2 pr-4 text-white">{r.utm_campaign || '—'}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2]">{r.utm_source || '—'}</td>
                          <td className="py-2 pr-4 text-[#f2f2f2] truncate max-w-[220px]"><a href={r.telegram_url} target="_blank" rel="noreferrer" className="underline">{r.telegram_url}</a></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}

        {tab === 'builder' && (
          <AdLinkBuilder countries={countries} />
        )}

        {tab === 'ads' && (
          <AdsAiAnalyst headers={headers} />
        )}

        {tab === 'countries' && (
          <Section
            title={`Country → Telegram Link Mappings (${countries.length})`}
            actions={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-white/50 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search country or code"
                    className="bg-white/[0.04] border border-white/10 rounded-full pl-8 pr-3 py-1.5 text-[12px] text-white outline-none w-56"
                  />
                </div>
                <button
                  onClick={() => setEditRow({ code: '', name: '', url: '', is_active: true, _new: true })}
                  className="btn-orange inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px]"
                >
                  <Plus className="w-3.5 h-3.5" /> Add country
                </button>
              </div>
            }
          >
            {editRow && <EditRowForm row={editRow} onCancel={() => setEditRow(null)} onSave={saveCountry} />}

            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                    <th className="py-2 pr-4">Code</th>
                    <th className="py-2 pr-4">Country</th>
                    <th className="py-2 pr-4">Telegram URL</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCountries.map((c) => (
                    <tr key={c.code} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-white font-semibold">{c.code}</td>
                      <td className="py-2 pr-4 text-white">{c.name}</td>
                      <td className="py-2 pr-4 text-[#f2f2f2] truncate max-w-[320px]"><a href={c.url} target="_blank" rel="noreferrer" className="underline">{c.url}</a></td>
                      <td className="py-2 pr-4">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-400/15 text-emerald-300' : 'bg-white/10 text-[#f2f2f2]'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <button onClick={() => setEditRow({ ...c })} className="text-[#f2f2f2] hover:text-white p-1.5 inline-flex"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => deleteCountry(c.code)} className="text-red-300 hover:text-red-200 p-1.5 inline-flex"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function EditRowForm({ row, onCancel, onSave }) {
  const [form, setForm] = useState(row);
  useEffect(() => setForm(row), [row]);
  const isNew = !!row._new;

  return (
    <div className="mt-2 mb-4 p-4 rounded-xl border border-orange-400/30 bg-white/[0.03]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Code (ISO)</label>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            maxLength={3}
            disabled={!isNew}
            placeholder="SG"
            className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none disabled:opacity-60"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Country Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Singapore"
            className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
          />
        </div>
        <div className="md:col-span-5">
          <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Telegram URL</label>
          <input
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://t.me/..."
            className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
          />
        </div>
        <div className="md:col-span-1 flex items-center gap-2">
          <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          <span className="text-[#f2f2f2] text-[12px]">Active</span>
        </div>
        <div className="md:col-span-1 flex items-center justify-end gap-2">
          <button onClick={() => onSave(form)} className="btn-orange inline-flex items-center gap-1 px-3 py-2 text-[12px]"><Save className="w-3.5 h-3.5" /> Save</button>
          <button onClick={onCancel} className="btn-outline-dark inline-flex items-center gap-1 px-2.5 py-2 text-[12px]"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function UtmBreakdownTable({ title, rows }) {
  return (
    <Section title={title}>
      {(!rows || rows.length === 0) ? (
        <div className="text-[#f2f2f2] text-sm">No data yet for this period.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                <th className="py-2 pr-4">Value</th>
                <th className="py-2 pr-4 text-right">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-white break-all">{r.value}</td>
                  <td className="py-2 pr-4 text-right text-white font-semibold">{r.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function AdLinkBuilder({ countries }) {
  const baseUrlDefault = typeof window !== 'undefined' ? window.location.origin : 'https://roadtobali.mbealert.com';
  const [baseUrl, setBaseUrl] = useState(baseUrlDefault);
  const [source, setSource] = useState('fb');
  const [medium, setMedium] = useState('cpc');
  const [campaign, setCampaign] = useState('roadtobali');
  const [content, setContent] = useState('');
  const [term, setTerm] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [copied, setCopied] = useState(false);

  function slug(v) {
    return String(v || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '');
  }

  function buildUrl() {
    const params = new URLSearchParams();
    if (source) params.set('utm_source', slug(source));
    if (medium) params.set('utm_medium', slug(medium));
    const camp = countryCode ? `${slug(campaign)}_${slug(countryCode)}` : slug(campaign);
    if (camp) params.set('utm_campaign', camp);
    if (content) params.set('utm_content', slug(content));
    if (term) params.set('utm_term', slug(term));
    const qs = params.toString();
    return baseUrl.replace(/\/$/, '') + (qs ? `/?${qs}` : '/');
  }

  const url = buildUrl();

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      alert('Copy failed');
    }
  }

  function quickFill(c) {
    setCountryCode(c.code);
    setContent(slug(c.name));
  }

  return (
    <div className="space-y-5">
      <Section title="Ad Link Builder" actions={<div className="text-[11.5px] text-[#f2f2f2]"><Link2 className="w-3.5 h-3.5 inline mr-1" /> Generate UTM tagged URLs for each ad set</div>}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Landing Page URL</label>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">utm_source</label>
            <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="fb" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">utm_medium</label>
            <input value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="cpc" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>

          <div className="md:col-span-4">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">utm_campaign (base)</label>
            <input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="roadtobali" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Country code</label>
            <input value={countryCode} onChange={(e) => setCountryCode(e.target.value.toUpperCase())} placeholder="SG" maxLength={3} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">utm_content (ad name)</label>
            <input value={content} onChange={(e) => setContent(e.target.value)} placeholder="hero_video_v1" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">utm_term (optional)</label>
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="broad" className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
        </div>

        <div className="mt-5 p-4 rounded-xl border border-orange-400/30 bg-white/[0.03]">
          <div className="text-[#f2f2f2] text-[11px] uppercase tracking-wider mb-2">Generated URL</div>
          <div className="flex items-start gap-3 flex-wrap">
            <code className="text-white text-[13px] break-all flex-1">{url}</code>
            <button onClick={copy} className="btn-orange inline-flex items-center gap-1.5 px-3 py-2 text-[12px]">
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </Section>

      <Section title="Quick fill from mapped countries" actions={<div className="text-[11.5px] text-[#f2f2f2]">Click a country to prefill</div>}>
        {countries.length === 0 ? (
          <div className="text-[#f2f2f2] text-sm">No countries configured yet.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => (
              <button key={c.code} onClick={() => quickFill(c)} className="liquid-glass rounded-full inline-flex items-center gap-2 px-3 py-1.5 text-[12px]">
                <span className="text-white font-semibold relative z-[1]">{c.code}</span>
                <span className="text-[#f2f2f2] relative z-[1]">{c.name}</span>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Generate for ALL countries" actions={<span className="text-[11.5px] text-[#f2f2f2]">One row per country with same campaign template</span>}>
        {countries.length === 0 ? (
          <div className="text-[#f2f2f2] text-sm">No countries configured yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Country</th>
                  <th className="py-2 pr-4">Generated URL</th>
                  <th className="py-2 pr-4 text-right">Copy</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((c) => {
                  const params = new URLSearchParams();
                  if (source) params.set('utm_source', slug(source));
                  if (medium) params.set('utm_medium', slug(medium));
                  params.set('utm_campaign', `${slug(campaign)}_${slug(c.code)}`);
                  if (content) params.set('utm_content', slug(content));
                  const rowUrl = baseUrl.replace(/\/$/, '') + `/?${params.toString()}`;
                  return (
                    <tr key={c.code} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-white font-semibold">{c.code}</td>
                      <td className="py-2 pr-4 text-white">{c.name}</td>
                      <td className="py-2 pr-4 text-[#f2f2f2] break-all">{rowUrl}</td>
                      <td className="py-2 pr-4 text-right">
                        <button onClick={() => navigator.clipboard.writeText(rowUrl)} className="btn-outline-dark inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px]">
                          <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}


function AdsAiAnalyst({ headers }) {
  const [days, setDays] = useState(7);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  function buildParams() {
    const p = { days };
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }

  async function loadData() {
    setLoading(true); setError('');
    try {
      const params = buildParams();
      const [f, c] = await Promise.all([
        axios.get(`${API}/admin/ads/funnel`, { headers, params }),
        axios.get(`${API}/admin/ads/campaigns`, { headers, params }),
      ]);
      setFunnel(f.data);
      setCampaigns(c.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load Meta Ads data');
    } finally {
      setLoading(false);
    }
  }

  async function runAnalysis() {
    setAnalyzing(true); setError('');
    try {
      const params = buildParams();
      const r = await axios.post(`${API}/admin/ads/analyze`, null, { headers, params });
      setAnalysis(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [days, startDate, endDate]);

  function applyPreset(d) {
    setStartDate(''); setEndDate(''); setDays(d);
  }
  function clearRange() {
    setStartDate(''); setEndDate('');
  }

  const camps = campaigns?.data || [];
  const usingRange = !!(startDate && endDate);

  return (
    <div className="space-y-5">
      <Section title="Period" actions={
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[#f2f2f2] text-[12px]">Quick:</span>
          {[3, 7, 14, 30].map((d) => (
            <button key={d} onClick={() => applyPreset(d)} className={`px-3 py-1.5 rounded-full text-[12px] ${(!usingRange && days === d) ? 'bg-white text-black font-semibold' : 'liquid-glass text-[#f2f2f2]'}`}>
              <span className="relative z-[1]">{d}d</span>
            </button>
          ))}
          <button onClick={() => loadData()} disabled={loading} className="btn-outline-dark inline-flex items-center gap-2 px-3 py-1.5 text-[12px]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button onClick={runAnalysis} disabled={analyzing} className="btn-orange inline-flex items-center gap-2 px-4 py-2 text-[12px]">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} {analyzing ? 'Analyzing…' : 'Analyze My Ads'}
          </button>
        </div>
      }>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[#f2f2f2] text-[11px] uppercase tracking-wider">End date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
          </div>
          <div className="md:col-span-3 flex items-end">
            <button onClick={clearRange} className="btn-outline-dark inline-flex items-center gap-1.5 px-3 py-2 text-[12px]">
              <X className="w-3.5 h-3.5" /> Clear range
            </button>
          </div>
          <div className="md:col-span-3 flex items-end">
            <div className="text-[#f2f2f2] text-[12px]">
              {usingRange ? (
                <>Showing <span className="text-white font-semibold">{startDate}</span> → <span className="text-white font-semibold">{endDate}</span></>
              ) : (
                <>Showing <span className="text-white font-semibold">last {days} days</span></>
              )}
            </div>
          </div>
        </div>
      </Section>

      {error && <div className="card-dark p-4 text-red-300 text-sm">{error}</div>}

      {/* AI recommendations */}
      {analysis && (
        <Section title="AI Recommendations">
          <div className="text-[#f2f2f2] text-[13px] whitespace-pre-wrap leading-relaxed">{analysis.recommendations}</div>
        </Section>
      )}

      {/* True funnel: FB → Landing → Telegram */}
      <Section title="True funnel by country (FB → Landing Page → Telegram)">
        {funnel?.fb_error ? (
          <div className="text-yellow-300 text-sm">Meta API error: {funnel.fb_error}. Landing / Telegram numbers still shown.</div>
        ) : null}
        {!funnel?.rows?.length ? (
          <div className="text-[#f2f2f2] text-sm">No data yet for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                  <th className="py-2 pr-4">Country</th>
                  <th className="py-2 pr-4">Spend</th>
                  <th className="py-2 pr-4">Impressions</th>
                  <th className="py-2 pr-4">FB link clicks</th>
                  <th className="py-2 pr-4">Landing page views</th>
                  <th className="py-2 pr-4">Telegram clicks</th>
                  <th className="py-2 pr-4">CVR (LP→TG)</th>
                  <th className="py-2 pr-4">Cost per Telegram click</th>
                </tr>
              </thead>
              <tbody>
                {funnel.rows.map((r) => (
                  <tr key={r.country_code} className="border-b border-white/5">
                    <td className="py-2 pr-4 text-white font-semibold">{r.country_code}</td>
                    <td className="py-2 pr-4 text-white">${(r.spend || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{(r.impressions || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{(r.fb_link_clicks || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-white">{(r.landing_pageviews || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-white font-semibold">{(r.telegram_clicks || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-[#ff9b5a]">{(r.conversion_rate || 0).toFixed(1)}%</td>
                    <td className="py-2 pr-4 text-white">{r.cost_per_telegram_click != null ? `$${r.cost_per_telegram_click.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Ad sets */}
      <Section title="Ad sets performance (from Meta)">
        {!camps.length ? (
          <div className="text-[#f2f2f2] text-sm">No ad sets found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#f2f2f2] text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                  <th className="py-2 pr-4">Campaign</th>
                  <th className="py-2 pr-4">Ad set</th>
                  <th className="py-2 pr-4">Spend</th>
                  <th className="py-2 pr-4">Impr.</th>
                  <th className="py-2 pr-4">Link clicks</th>
                  <th className="py-2 pr-4">CTR</th>
                  <th className="py-2 pr-4">CPM</th>
                  <th className="py-2 pr-4">CPC</th>
                  <th className="py-2 pr-4">Freq.</th>
                </tr>
              </thead>
              <tbody>
                {camps.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 pr-4 text-white">{r.campaign_name || '—'}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{r.adset_name || '—'}</td>
                    <td className="py-2 pr-4 text-white">${parseFloat(r.spend || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{parseInt(r.impressions || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-white">{parseInt(r.inline_link_clicks || r.clicks || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{parseFloat(r.ctr || 0).toFixed(2)}%</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">${parseFloat(r.cpm || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">${parseFloat(r.cpc || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 text-[#f2f2f2]">{parseFloat(r.frequency || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

